import { useState } from 'react'
import { useI18n } from '../lib/i18n'
import { LangSelector } from './LangSelector'
import { controlPlane } from '../lib/client'
import { HumanCheck } from './HumanCheck'

/**
 * Entrada: senha (padrão), cadastro, ou recuperação por link mágico.
 *
 * O cadastro pede e-mail+senha e entra NA HORA — sem abrir e-mail nenhum.
 * O e-mail continua sendo pedido, mas só é efetivamente USADO (um e-mail
 * de verdade sai) se a senha se perder depois: o link mágico não some,
 * muda de papel — deixa de ser "como todo mundo entra na primeira vez" e
 * vira especificamente "esqueci a senha" (internal/auth/auth.go tem o
 * raciocínio completo sobre por que o link continua existindo: sem um
 * caminho de volta que não dependa da senha, senha perdida seria conta
 * perdida).
 *
 * O que se pede no cadastro é um APELIDO, não um indicativo. Exigir
 * indicativo oficial fazia duas coisas erradas de uma vez: deixava de fora
 * quem não tem classe e quem só quer a Faixa do Cidadão — onde indicativo
 * nem existe — e deixava qualquer pessoa digitar o indicativo de qualquer
 * radioamador do país, que é registro público. Indicativo oficial não se
 * escolhe: reivindica-se depois, comprovando a licença
 * (components/Identidade).
 */

export interface User {
  id: string
  callsign: string
  class: string
  /** livre (apelido), simulado (emitido na prova) ou oficial (comprovado). */
  kind: 'livre' | 'simulado' | 'oficial'
  verified: boolean
  joined_at: string
  nota_identidade: string
  /** Se já tem senha, a tela oferece entrar por ela em vez de pelo link. */
  tem_senha?: boolean
  /** Administra. Vem do servidor, de variável de ambiente — nunca do cliente. */
  admin?: boolean
}

interface LoginResponse {
  message?: string
  dev_link?: string
  code?: string
}

export function Login({
  onEntered,
  aviso,
}: {
  onEntered: (u: User) => void
  /** Motivo de a pessoa ter voltado para cá — link expirado, por exemplo. */
  aviso?: string | null
}) {
  const { t } = useI18n()
  // Senha primeiro: quem já tem conta volta por aqui, e abrir a caixa de
  // e-mail a cada visita é o atrito que a senha existe para remover.
  const [modo, setModo] = useState<'senha' | 'cadastro' | 'recuperar'>('senha')
  const [login, setLogin] = useState('')
  const [senha, setSenha] = useState('')
  const [email, setEmail] = useState('')
  const [callsign, setCallsign] = useState('')
  const [senhaCadastro, setSenhaCadastro] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [devLink, setDevLink] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [humanoOk, setHumanoOk] = useState(false)
  const [humanoProva, setHumanoProva] = useState<{ token: string; valor: number } | null>(null)
  // Bump pra remontar o HumanCheck e pedir um desafio novo: o token antigo
  // já foi consumido no servidor de qualquer jeito (um-tiro-só).
  const [tentativaHumano, setTentativaHumano] = useState(0)

  const trocarModo = (m: typeof modo) => {
    setModo(m)
    setErr(null)
  }

  /** "Esqueci a senha": pede só o e-mail — o apelido não importa aqui, o
   * servidor já ignora esse campo quando a conta existe. */
  const request = async () => {
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, callsign: '' }),
      })
      const d = (await r.json()) as LoginResponse
      if (!r.ok) {
        setErr(d.message ?? t.login.erroEnviarLink)
        return
      }
      setSent(d.message ?? t.login.linkEnviado)
      setDevLink(d.dev_link ?? null)
    } catch {
      setErr(t.login.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  /** Cadastro: cria a conta e já entra, sem passar pelo e-mail. */
  const cadastrar = async () => {
    if (senhaCadastro !== confirmarSenha) {
      setErr(t.login.senhasNaoConferem)
      return
    }
    // Destrava o AudioContext AGORA, dentro do clique — sem isto, ligar o
    // áudio sozinho depois de entrar cairia fora da janela de gesto do
    // usuário em navegadores restritivos (iOS Safari é regra dura aqui).
    controlPlane.unlockAudio()
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch('/api/auth/cadastro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, callsign, senha: senhaCadastro }),
      })
      const d = (await r.json()) as { user?: User; message?: string; code?: string }
      if (!r.ok || !d.user) {
        setErr(d.message ?? t.login.erroCriarConta)
        return
      }
      onEntered(d.user)
    } catch {
      setErr(t.login.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  const entrar = async () => {
    // Mesma razão de cadastrar(): destravar o AudioContext precisa acontecer
    // dentro do clique, antes de qualquer await.
    controlPlane.unlockAudio()
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch('/api/auth/entrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login,
          senha,
          ...(humanoProva
            ? { desafio_token: humanoProva.token, desafio_valor: humanoProva.valor }
            : {}),
        }),
      })
      const d = (await r.json()) as { user?: User; message?: string; code?: string }
      if (!r.ok || !d.user) {
        setErr(d.message ?? t.login.erroEntrar)
        // Conta que só existe por link: manda para a recuperação, que é o
        // caminho que funciona, em vez de deixar a pessoa tentando senha
        // que nunca existiu.
        if (d.code === 'sem_senha') {
          setModo('recuperar')
          setEmail(login.includes('@') ? login : '')
        }
        // O token do desafio já foi consumido no servidor mesmo tendo
        // falhado — pede um novo em vez de deixar a pessoa presa.
        if (d.code === 'desafio_invalido') {
          setHumanoOk(false)
          setHumanoProva(null)
          setTentativaHumano((n) => n + 1)
        }
        return
      }
      onEntered(d.user)
    } catch {
      setErr(t.login.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  // Em desenvolvimento o link vem na resposta; entrar direto poupa copiar e colar.
  const enterWithDevLink = async () => {
    if (!devLink) return
    const token = new URL(devLink).searchParams.get('token')
    if (!token) return
    controlPlane.unlockAudio()
    setBusy(true)
    try {
      const r = await fetch(`/api/auth/verify?token=${encodeURIComponent(token)}`)
      const d = (await r.json()) as { user?: User; message?: string }
      if (!r.ok || !d.user) {
        setErr(d.message ?? t.login.linkInvalido)
        return
      }
      onEntered(d.user)
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <section className="login">
        <LangSelector />
        <h1>{t.login.confereEmail}</h1>
        <p className="login-sent">{sent}</p>

        {devLink && (
          <div className="login-dev">
            <strong>{t.login.modoDev}</strong>
            <p>{t.login.modoDevTexto}</p>
            <button onClick={() => void enterWithDevLink()} disabled={busy}>
              {t.login.entrarComLink}
            </button>
          </div>
        )}

        <button className="link" onClick={() => setSent(null)}>
          {t.login.usarOutroEmail}
        </button>
        {err && <p className="denial">{err}</p>}
      </section>
    )
  }

  if (modo === 'cadastro') {
    return (
      <section className="login">
        <LangSelector />
        <h1>{t.login.titulo}</h1>
        {aviso && <p className="denial">{aviso}</p>}

        <p className="login-intro">{t.login.cadastroIntro}</p>

        <label htmlFor="callsign">{t.login.comoChamar}</label>
        <input
          id="callsign"
          value={callsign}
          onChange={(e) => setCallsign(e.target.value.toUpperCase())}
          placeholder={t.login.apelidoPlaceholder}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={12}
        />
        <p className="login-hint">
          {t.login.cadastroHintPre} <b>{t.login.cadastroHintForte}</b>{' '}
          {t.login.cadastroHintPos}
        </p>

        <label htmlFor="email-cadastro">{t.login.email}</label>
        <input
          id="email-cadastro"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.login.emailPlaceholder}
        />

        <label htmlFor="senha-cadastro">{t.login.senha}</label>
        <input
          id="senha-cadastro"
          type="password"
          autoComplete="new-password"
          value={senhaCadastro}
          onChange={(e) => setSenhaCadastro(e.target.value)}
        />

        <label htmlFor="confirmar-senha">{t.login.confirmarSenha}</label>
        <input
          id="confirmar-senha"
          type="password"
          autoComplete="new-password"
          value={confirmarSenha}
          onChange={(e) => setConfirmarSenha(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && email && callsign && senhaCadastro && confirmarSenha) {
              void cadastrar()
            }
          }}
        />

        {err && <p className="denial">{err}</p>}

        <button
          className="login-go"
          onClick={() => void cadastrar()}
          disabled={busy || !email || !callsign || !senhaCadastro || !confirmarSenha}
        >
          {busy ? t.login.criando : t.login.criarConta}
        </button>

        <button className="link" onClick={() => trocarModo('senha')}>
          {t.login.jaTenhoConta}
        </button>
      </section>
    )
  }

  if (modo === 'recuperar') {
    return (
      <section className="login">
        <LangSelector />
        <h1>{t.login.titulo}</h1>
        {aviso && <p className="denial">{aviso}</p>}

        <p className="login-intro">{t.login.recuperarIntro}</p>

        <label htmlFor="email-recuperar">{t.login.email}</label>
        <input
          id="email-recuperar"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.login.emailPlaceholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && email) void request()
          }}
        />

        {err && <p className="denial">{err}</p>}

        <button
          className="login-go"
          onClick={() => void request()}
          disabled={busy || !email}
        >
          {busy ? t.login.enviando : t.login.receberLink}
        </button>

        <button className="link" onClick={() => trocarModo('senha')}>
          {t.login.jaTenhoSenha}
        </button>
      </section>
    )
  }

  return (
    <section className="login">
      <LangSelector />
      <h1>{t.login.titulo}</h1>
      {aviso && <p className="denial">{aviso}</p>}

      <label htmlFor="login">{t.login.loginLabel}</label>
      <input
        id="login"
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />

      <label htmlFor="senha">{t.login.senha}</label>
      <input
        id="senha"
        type="password"
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
        autoComplete="current-password"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && login && senha && humanoOk) void entrar()
        }}
      />

      <HumanCheck
        key={tentativaHumano}
        onChange={(ok, prova) => {
          setHumanoOk(ok)
          setHumanoProva(prova)
        }}
      />

      {err && <p className="denial">{err}</p>}

      <button
        className="login-go"
        onClick={() => void entrar()}
        disabled={busy || !login || !senha || !humanoOk}
      >
        {busy ? t.login.entrando : t.login.entrar}
      </button>

      <button className="link" onClick={() => trocarModo('cadastro')}>
        {t.login.naoTemConta}
      </button>
      <button className="link" onClick={() => trocarModo('recuperar')}>
        {t.login.esqueciSenha}
      </button>
    </section>
  )
}
